import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { AppConfigProvider } from './config/useAppConfig';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import AccessDenied from './components/AccessDenied';
import Dashboard from './pages/Dashboard';
import Listings from './pages/Listings';
import Bookings from './pages/Bookings';
import Users from './pages/Users';
import Financials from './pages/Financials';
import Settings from './pages/Settings';
import NotificationRules from './pages/NotificationRules';
import NotificationCenter from './pages/NotificationCenter';
import GuestProfileOverview from './pages/GuestProfileOverview';
import AddNewListing from './pages/AddNewListing';
import EditListing from './pages/EditListing';
import CreateBooking from './pages/CreateBooking';
import AddNewUser from './pages/AddNewUser';
import BookingDetails from './pages/BookingDetails';
import RecordExpense from './pages/RecordExpense';
import Reviews from './pages/Reviews';
import Schedule from './pages/Schedule';
import Content from './pages/Content';
import Login from './pages/Login';
import authService from './services/authService';

const ProtectedRoute = ({ children }) => {
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Renders AccessDenied if the user lacks the required permission.
// If permission is null/undefined, always renders children.
const PermissionRoute = ({ permission, children }) => {
  const { hasPermission } = useAuth();
  if (permission && !hasPermission(permission)) {
    return <AccessDenied />;
  }
  return children;
};

function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-background flex">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - Fixed Position */}
        <div className={`
          fixed inset-y-0 left-0 z-50 h-screen
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <Sidebar onClose={() => setSidebarOpen(false)} />
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex-1 lg:ml-64 min-h-screen">
          {/* Mobile Header with Hamburger */}
          <header className="lg:hidden h-16 bg-card border-b border-border px-4 flex items-center justify-between sticky top-0 z-30">
            <button
              onClick={() => setSidebarOpen(true)}
              className="w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
            >
              <Icon icon="lucide:menu" className="text-xl" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon icon="lucide:home" className="text-white text-lg" />
              </div>
              <span className="text-sm font-heading font-bold">RoarHomes Admin</span>
            </div>
          </header>

          {/* Routes - Scrollable Content */}
          <div className="overflow-y-auto">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/listings" element={<PermissionRoute permission="viewProperty"><Listings /></PermissionRoute>} />
              <Route path="/listings/add-new" element={<PermissionRoute permission="addProperty"><AddNewListing /></PermissionRoute>} />
              <Route path="/listings/edit/:id" element={<PermissionRoute permission="editProperty"><EditListing /></PermissionRoute>} />
              <Route path="/bookings" element={<PermissionRoute permission="viewBooking"><Bookings /></PermissionRoute>} />
              <Route path="/bookings/create" element={<PermissionRoute permission="addBooking"><CreateBooking /></PermissionRoute>} />
              <Route path="/bookings/edit/:id" element={<PermissionRoute permission="editBooking"><CreateBooking /></PermissionRoute>} />
              <Route path="/bookings/:id" element={<PermissionRoute permission="viewBooking"><BookingDetails /></PermissionRoute>} />
              <Route path="/users" element={<PermissionRoute permission="viewUser"><Users /></PermissionRoute>} />
              <Route path="/users/add-new" element={<PermissionRoute permission="addUser"><AddNewUser /></PermissionRoute>} />
              <Route path="/users/edit/:id" element={<PermissionRoute permission="editUser"><AddNewUser /></PermissionRoute>} />
              <Route path="/users/profile/:id" element={<PermissionRoute permission="viewUser"><GuestProfileOverview /></PermissionRoute>} />
              <Route path="/financials" element={<PermissionRoute permission="viewFinancialReports"><Financials /></PermissionRoute>} />
              <Route path="/financials/record-expense" element={<PermissionRoute permission="manageExpenses"><RecordExpense /></PermissionRoute>} />
              <Route path="/financials/edit-expense/:id" element={<PermissionRoute permission="manageExpenses"><RecordExpense /></PermissionRoute>} />
              <Route path="/settings" element={<PermissionRoute permission="viewSystemSettings"><Settings /></PermissionRoute>} />
              <Route path="/settings/notification-rules" element={<PermissionRoute permission="manageNotifications"><NotificationRules /></PermissionRoute>} />
              <Route path="/notifications" element={<NotificationCenter />} />
              <Route path="/reviews" element={<PermissionRoute permission="viewGuestDetails"><Reviews /></PermissionRoute>} />
              <Route path="/schedule" element={<PermissionRoute permission="viewBooking"><Schedule /></PermissionRoute>} />
              <Route path="/content" element={<Content />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AppConfigProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/*" element={<AppShell />} />
          </Routes>
        </Router>
      </AuthProvider>
    </AppConfigProvider>
  );
}

export default App;
